import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../api/apiHelper";
import logo from "../../assets/Mobile logo monogram - L.png";

const fmt = (n) =>
  (n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const StatCard = ({ label, value, prefix, sub }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-6 py-5 text-center">
    <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-2">{label}</p>
    <p className="text-3xl font-extrabold text-indigo-700">
      {prefix}{fmt(value)}
    </p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

const InfluencerPublicView = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // API_URL already includes /api/v1/ — matchmatrix router mounts at /api/v1/matchmatrix
        const baseUrl = API_URL.replace(/\/$/, "");
        const res = await axios.get(`${baseUrl}/matchmatrix/public/influencer/${token}`);
        if (res.data?.success) {
          setData(res.data);
        } else {
          setError("Influencer link not found or has been removed.");
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          setError("Influencer link not found or has been removed.");
        } else {
          setError("Something went wrong. Please try again later.");
        }
      }
      setLoading(false);
    };
    if (token) fetchData();
  }, [token]);

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-amber-50 flex items-center justify-center">
        <div className="text-center text-gray-400">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-200 border-t-indigo-600 mx-auto mb-4" />
          <p className="text-sm">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-amber-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-md p-10 text-center max-w-md w-full">
          <p className="text-4xl mb-4">🔗</p>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Link Not Found</h2>
          <p className="text-sm text-gray-500">{error}</p>
          <p className="text-xs text-gray-400 mt-4">
            For queries, contact{" "}
            <a href="mailto:support@matchmatrix.in" className="text-indigo-600 hover:underline">
              support@matchmatrix.in
            </a>
          </p>
        </div>
      </div>
    );
  }

  const { summary, transactions, weeklyBreakdown = [], payouts, influencer, commissionRate } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-amber-50">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-[#19224B]">
        {/* Decorative radial glow */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-amber-400/10 blur-3xl" />
          <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-indigo-500/20 blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[200px] bg-amber-300/5 blur-2xl rounded-full" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 pt-7 pb-8">
          {/* Brand bar */}
          <div className="flex items-center gap-3 mb-7">
            <img src={logo} alt="MatchMatrix" className="w-10 h-10 rounded-xl shadow-lg shadow-amber-500/30 ring-2 ring-amber-400/40" />
            <div>
              <span className="text-white font-extrabold text-base tracking-tight leading-none">MatchMatrix</span>
              <p className="text-amber-400/70 text-[10px] font-medium tracking-widest uppercase leading-none mt-0.5">Referral Partner</p>
            </div>
          </div>

          {/* Main content */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="text-amber-400 text-xs font-semibold uppercase tracking-widest mb-2">Your Earnings Dashboard</p>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-2">
                Hey <span className="text-amber-300">{influencer || "there"}</span> 🎉
              </h1>
              <p className="text-white/50 text-sm max-w-sm">
                Every plan sold through your code puts money in your pocket —&nbsp;
                <span className="text-amber-300 font-semibold">{commissionRate}% commission</span>, paid every Saturday.
              </p>
            </div>

            {/* Earnings pill */}
            <div className="flex-shrink-0">
              <div className="relative bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl px-6 py-4 shadow-xl shadow-amber-500/30 text-center">
                <div className="absolute inset-0 rounded-2xl bg-white/10 backdrop-blur-sm" />
                <div className="relative">
                  <p className="text-amber-900/70 text-[10px] font-bold uppercase tracking-widest mb-0.5">Total Earned</p>
                  <p className="text-[#19224B] text-3xl font-black leading-none">₹{fmt(summary.commissionEarned)}</p>
                  {summary.pendingPayout > 0 && (
                    <p className="text-amber-800/80 text-xs font-semibold mt-1.5">
                      ₹{fmt(summary.pendingPayout)} incoming this Sat 🚀
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom fade edge */}
        <div className="h-3 bg-gradient-to-b from-[#19224B] to-transparent" />
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Conversions" value={summary.conversions} colorClass="text-indigo-700" />
          <StatCard label="Revenue Generated" value={summary.revenue} prefix="₹" sub="total from your referrals" />
          <StatCard label="Commission Earned" value={summary.commissionEarned} prefix="₹" sub={`${commissionRate}% of revenue`} />
          <StatCard
            label="Pending Payout"
            value={summary.pendingPayout}
            prefix="₹"
            sub={summary.pendingPayout > 0 ? "will be paid soon" : "all paid!"}
          />
        </div>

        {/* Transactions */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">Referral Transactions</h2>
            <p className="text-xs text-gray-400 mt-0.5">Each plan purchased through your code</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {["#", "Plan", "Your Commission", "Date"].map((h) => (
                    <th key={h} className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-10 text-center text-gray-400 text-sm">
                      No conversions yet — share your code!
                    </td>
                  </tr>
                ) : (
                  transactions.map((t, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition">
                      <td className="px-5 py-3 text-gray-400 text-xs">{i + 1}</td>
                      <td className="px-5 py-3">
                        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs rounded font-mono font-medium">
                          {t.planKey || "Unknown Plan"}
                        </span>
                      </td>
                      <td className="px-5 py-3 font-semibold text-green-600">₹{fmt(t.commission)}</td>
                      <td className="px-5 py-3 text-gray-500 text-xs">{fmtDate(t.usedAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Weekly Breakdown */}
        {weeklyBreakdown.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-800">Weekly Breakdown</h2>
              <p className="text-xs text-gray-400 mt-0.5">Payout cycle runs Mon–Sun · Paid every Saturday</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {["Week", "Conversions", "Earned", "Paid Out", "Status"].map((h) => (
                      <th key={h} className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {weeklyBreakdown.map((w, i) => {
                    const pending = parseFloat((w.commission - w.paidOut).toFixed(2));
                    return (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition">
                        <td className="px-5 py-3 text-gray-600 text-xs whitespace-nowrap">
                          {fmtDate(w.weekStart)} – {fmtDate(w.weekEnd)}
                        </td>
                        <td className="px-5 py-3 font-semibold text-indigo-700">{w.conversions}</td>
                        <td className="px-5 py-3 font-semibold text-orange-600">₹{fmt(w.commission)}</td>
                        <td className="px-5 py-3 font-semibold text-teal-700">₹{fmt(w.paidOut)}</td>
                        <td className="px-5 py-3">
                          {pending <= 0 ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Settled ✓</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">₹{fmt(pending)} due Sat</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payouts */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">Payout History</h2>
            <p className="text-xs text-gray-400 mt-0.5">Payments made to you</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {["#", "Amount", "Method", "Date"].map((h) => (
                    <th key={h} className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payouts.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-10 text-center text-gray-400 text-sm">
                      No payouts recorded yet
                    </td>
                  </tr>
                ) : (
                  payouts.map((p, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition">
                      <td className="px-5 py-3 text-gray-400 text-xs">{i + 1}</td>
                      <td className="px-5 py-3 font-semibold text-teal-700">₹{fmt(p.amount)}</td>
                      <td className="px-5 py-3">
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded font-medium">
                          {p.method}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-gray-500 text-xs">{fmtDate(p.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Terms & Conditions */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <details>
            <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-700 text-sm flex items-center justify-between list-none select-none hover:bg-gray-50 transition">
              <span>Influencer Programme Terms &amp; Conditions</span>
              <span className="text-gray-400 text-xs">tap to expand</span>
            </summary>
            <div className="px-6 pb-6 space-y-4 text-sm text-gray-600 border-t border-gray-100 pt-4">
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">1. Commission Structure</h4>
                <p>You earn <strong>{commissionRate}%</strong> of the final amount paid by each user who purchases a MatchMatrix plan using your referral code. Commission is calculated on the net amount received by MatchMatrix after any discounts.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">2. Payout Schedule</h4>
                <p>Commissions are paid <strong>weekly, every Saturday by 12:00 PM IST</strong>. The payout cycle runs Monday to Sunday. Only confirmed conversions (completed payments) count toward your weekly earnings. Refunded transactions are deducted.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">3. Minimum Payout Threshold</h4>
                <p>A minimum of <strong>₹1</strong> in pending commission is required to trigger a payout. Amounts below this threshold roll over to the next cycle.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">4. Payment Method</h4>
                <p>Payouts are made via UPI to the handle registered with MatchMatrix. It is your responsibility to ensure your UPI handle is accurate and active. Failed transfers due to incorrect details will be retried the following Saturday.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">5. Prohibited Activities</h4>
                <p>You may not use your own referral code to purchase plans for yourself or others. Self-referral conversions will be reversed. Fraudulent activity will result in immediate termination of your partnership and forfeiture of pending commissions.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">6. Code Ownership</h4>
                <p>Your referral code is personal and non-transferable. MatchMatrix reserves the right to deactivate a code at any time. Active conversions prior to deactivation will still be honoured.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">7. Tax Liability</h4>
                <p>Commission income may be subject to income tax under applicable Indian law. You are responsible for declaring this income. MatchMatrix will provide payout records on request for your tax filings.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">8. Programme Changes</h4>
                <p>MatchMatrix reserves the right to change commission rates or terms with 7 days' notice communicated to your registered contact. Continued use of your referral code after the notice period constitutes acceptance of the revised terms.</p>
              </div>
              <p className="text-xs text-gray-400 pt-2 border-t border-gray-100">
                For questions or disputes, email{" "}
                <a href="mailto:support@matchmatrix.in" className="text-indigo-600 hover:underline">support@matchmatrix.in</a>.
                Last updated: May 2026.
              </p>
            </div>
          </details>
        </div>

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-xs text-gray-400">
            Questions? Contact us at{" "}
            <a href="mailto:support@matchmatrix.in" className="text-indigo-600 hover:underline font-medium">
              support@matchmatrix.in
            </a>
          </p>
          <p className="text-xs text-gray-300 mt-1">© MatchMatrix — All rights reserved</p>
        </div>
      </div>
    </div>
  );
};

export default InfluencerPublicView;
